import React from "react";
import { Box, Typography, Container, Button } from "@mui/material";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { PortfolioGrid } from "../portfolio/PortfolioGrid";
import { Gallery } from "../portfolio/GalleryCard";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";

interface FeaturedGalleriesProps {
  galleries: Gallery[];
  onViewAll?: () => void;
}

export const FeaturedGalleries: React.FC<FeaturedGalleriesProps> = ({
  galleries,
  onViewAll,
}) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  return (
    <Box
      ref={ref}
      component={motion.div}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : { opacity: 0, y: 40 }}
      transition={{ duration: 0.6 }}
      sx={{
        pt: { xs: 4, md: 6 },
        pb: { xs: 2, md: 3 },
        backgroundColor: "background.default",
      }}
    >
      <Container maxWidth="lg">
        {/* Section Header - Centered */}
        <Box
          sx={{
            textAlign: "center",
            mb: { xs: 3, md: 4 },
          }}
        >
          <Typography
            variant="h1"
            sx={{
              fontFamily: '"Cinzel Decorative", serif',
              fontSize: { xs: "2rem", sm: "2.5rem", md: "3.5rem", lg: "4rem" },
              fontWeight: 400,
              mb: { xs: 2, md: 3 },
              letterSpacing: "-0.02em",
              lineHeight: 1.1,
            }}
          >
            Featured Galleries
          </Typography>
          {onViewAll && (
            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mb: { xs: 3, md: 4 },
              }}
            >
              <Button
                variant="text"
                endIcon={<ArrowForwardIcon />}
                onClick={onViewAll}
                disableRipple
                sx={{
                  color: "text.primary",
                  border: "none",
                  boxShadow: "none",
                  padding: "12px 24px",
                  fontSize: { xs: "0.875rem", md: "1rem" },
                  fontFamily: '"Playfair Display", serif',
                  fontWeight: 400,
                  letterSpacing: "0.02em",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "transparent",
                    border: "none",
                    boxShadow: "none",
                    transform: "translateY(-2px)",
                  },
                }}
              >
                View All Galleries
              </Button>
            </Box>
          )}
        </Box>

        {/* Portfolio Grid - Limit to 3 featured galleries */}
        <PortfolioGrid galleries={galleries.slice(0, 3)} />
      </Container>
    </Box>
  );
};
